/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  Flex,
  Group,
  Skeleton,
  Stack,
  TextInput,
  Title,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { RiAddLine, RiCloseLine } from '@remixicon/react';
import { Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Tag from '@fai/ui/components/tag';
import { TitleVariants } from '@fai/ui/components/typography/variants';

import { EllipsisMenu, EllipsisMenuItem } from './ellipsis-menu';

export default function DEProjectSubHeader({
  titleSlot,
  backButtonSlot,
  bottomSlot,
}: {
  titleSlot: ReactNode;
  backButtonSlot?: ReactNode;
  bottomSlot?: ReactNode;
}) {
  const theme = useMantineTheme();

  return (
    <Stack align="flex-start" gap={theme.pf.layout['2xs']}>
      {backButtonSlot}

      <Suspense fallback={<Skeleton h={rem(32)} />}>
        <Stack gap={theme.pf.layout['2xs']} w="100%">
          {titleSlot}
          {bottomSlot}
        </Stack>
      </Suspense>
    </Stack>
  );
}

DEProjectSubHeader.displayName = 'DEProjectSubHeader';
DEProjectSubHeader.TitleRow = TitleRow;
DEProjectSubHeader.EllipsisMenu = EllipsisMenu;
DEProjectSubHeader.EllipsisMenuItem = EllipsisMenuItem;
DEProjectSubHeader.Labels = Labels;

function TitleRow({
  title,
  titleSuffixSlot,
  leftSlot,
  rightSlot,
}: {
  title: string;
  titleSuffixSlot?: ReactNode;
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
}) {
  const theme = useMantineTheme();

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      justify="space-between"
      gap={theme.pf.layout.xs}
      w="100%"
      wrap="nowrap"
    >
      <Flex direction={{ base: 'column', md: 'row' }} gap={theme.pf.layout.xs} wrap="nowrap">
        <Group gap={theme.pf.layout.xs} wrap="nowrap">
          <Title
            c={theme.pf.color.fg.neutral.strong}
            variant={TitleVariants.Xl}
            fz={rem(28)}
            style={{ flexShrink: 1, wordBreak: 'break-all' }}
          >
            {title}
          </Title>

          {titleSuffixSlot}
        </Group>

        <Group gap={theme.pf.layout['3xs']} wrap="nowrap">
          {leftSlot}
        </Group>
      </Flex>

      <Group gap={theme.pf.layout['3xs']} wrap="nowrap">
        {rightSlot}
      </Group>
    </Flex>
  );
}

// TODO: it can be relocated in upper directory.
function Labels({
  labels,
  addLabel,
  deleteLabel,
}: {
  labels: string[];
  addLabel: (label: string) => void;
  deleteLabel: (label: string) => void;
}) {
  const theme = useMantineTheme();

  return (
    <Group gap={theme.pf.layout['4xs']}>
      {labels.map((label) => (
        <Tag key={label} bg={theme.pf.color.fg.white}>
          <Group gap={theme.pf.layout['4xs']} component="span">
            {label}

            <RiCloseLine
              color={theme.pf.color.fg.neutral.subtle}
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={() => deleteLabel(label)}
            />
          </Group>
        </Tag>
      ))}

      <InputTag addLabel={addLabel} />
    </Group>
  );
}

// TODO: fix style of InputTag
function InputTag({ addLabel }: { addLabel: (value: string) => void }) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [isInput, setIsInput] = useState<boolean>(false);
  const [inputString, setInputString] = useState<string>('');

  return isInput ? (
    <TextInput
      autoFocus
      unstyled
      placeholder="Placeholder"
      styles={{
        wrapper: { height: rem(24) },
        input: {
          border: `${rem(1)} solid ${theme.pf.color.border.neutral.subtle}`,
          borderRadius: theme.pf.radius.sm,
          padding: `0 ${theme.pf.layout['3xs']}`,
          width: rem(100),
          height: rem(24),
          fontSize: rem(14),
        },
      }}
      onBlur={() => {
        setIsInput(false);
        setInputString('');
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          addLabel(inputString);
        }
      }}
    />
  ) : (
    <Tag bg={theme.pf.color.fg.white} px={0} size="xs" h={rem(24)}>
      <Group
        component="span"
        gap={theme.pf.layout['4xs']}
        px={theme.pf.layout['3xs']}
        style={{ cursor: 'pointer' }}
        onClick={() => setIsInput(true)}
      >
        <RiAddLine size={16} />
        {t('dedicated_endpoints.project.add_labels')}
      </Group>
    </Tag>
  );
}
